import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    addClientFlag,
    addClientInsuranceDetails,
    addClientMemo,
    addClientSession,
    addCollectionsNote,
    addCollectionsNoteDocuments,
    addCommunicationNote,
    deleteCommunicationNote,
    editCommunicationNote,
    deleteCollectionsNote,
    deleteCollectionsNoteDocuments,
    editCollectionsNote,
    addPaymentCard,
    addPaymentCardLabel,
    clientDataImportDiagnosis,
    deleteClientDocument,
    deleteClientFlags,
    deleteClientInsuranceInformation,
    deleteClientSession,
    deleteCreditCards,
    deleteInsuranceAttachment,
    editClientCurrentFee,
    editClientInsuranceDetails,
    editClientSession,
    generatePaymentLink,
    updateIntakeClient,
    updateClientFlag,
    addClientTag,
    removeClientTag,
    setPreferredPaymentCard,
    validatePaymentCard,
} from "../services/clients.services";
import {
    IAddClientFlag,
    IAddClientSession,
    IAddClientSessionResponse,
    IClientCreditCardDetails,
    IClientDocument,
    IClientEditInsuranceDetails,
    IClientFee,
    IClientInsuranceAttachment,
    IClientInsuranceDetails,
    IClientIntakeImportDiagnosisPayload,
    IClientIntakeImportDiagnosisResponse,
    IClientMemo,
    IClientNewInsuranceDetails,
    IDeleteCardsPayload,
    IDeleteClientSession,
    IDeleteClientSessionResponse,
    IDeleteCreditCardsResponse,
    IDeleteInsuranceInformationPayload,
    IDeleteInsuranceInformationResponse,
    IEditClientFee,
    IEditClientSession,
    IEditClientSessionResponse,
    IGeneratePaymentLinkPayload,
    IGeneratePaymentLinkResponse,
    INewCreditCardDetails,
    INewMemo,
    IValidateCardPayload,
    ISetPreferredCardPayload,
    ICollectionsNoteDocument,
    ICollectionsNoteRecord,
    ICommunicationNote,
    INewCommunicationNoteRecord,
    IClientTag,
    ClientTagKey,
} from "../types/clients.types";
import { IApiResponse } from "../../../utils/types/api-response";
import { IUpdateClientOnIntake } from "../../../utils/types/client";

// Hook for deleting client document
export function useDeleteClientDocument(clientId: string, documentId: string) {
    interface IApiResponse {
        message: string;
        documents: IClientDocument[];
    }
    return useMutation<IApiResponse, AxiosError<IApiResponse>, null>({
        mutationKey: ["delete-document", clientId],
        mutationFn: () => deleteClientDocument(clientId, documentId),
    });
}

export function useAddPaymentCard(clientId: string) {
    return useMutation<
        IApiResponse<IClientCreditCardDetails>,
        AxiosError<IApiResponse<IClientCreditCardDetails>>,
        INewCreditCardDetails
    >({
        mutationKey: ["add-card", clientId],
        mutationFn: (data: INewCreditCardDetails) =>
            addPaymentCard(clientId, data),
    });
}

export function useAddClientInsurance(clientId: string) {
    return useMutation<
        IApiResponse<IClientInsuranceDetails>,
        AxiosError<IApiResponse<IClientInsuranceDetails>>,
        IClientNewInsuranceDetails
    >({
        mutationKey: ["add-insurance", clientId],
        mutationFn: (data: IClientNewInsuranceDetails) =>
            addClientInsuranceDetails(clientId, data),
    });
}

// Hook to add card labels
export function useAddCardLabel(clientId: string, paymentCardId: string) {
    interface ApiResponseData {
        message: string;
    }
    interface Payload {
        card_label: string;
    }
    return useMutation<
        IApiResponse<ApiResponseData>,
        AxiosError<IApiResponse<ApiResponseData>>,
        Payload
    >({
        mutationKey: ["add-card-label", paymentCardId],
        mutationFn: (payload: Payload) =>
            addPaymentCardLabel(clientId, paymentCardId, payload),
    });
}

// Hook to validate credit card
export function useValidateCard() {
    interface ApiResponseData {
        message: string;
        data: Record<string, string>;
    }
    return useMutation<
        ApiResponseData,
        AxiosError<ApiResponseData>,
        IValidateCardPayload
    >({
        mutationFn: (payload: IValidateCardPayload) =>
            validatePaymentCard(payload),
        mutationKey: ["validate-card"],
    });
}

// Hook to set or reset a client's current credit card
export function useSetPreferredCard() {
    interface ApiResponseData {
        message: string;
    }
    return useMutation<
        ApiResponseData,
        AxiosError<ApiResponseData>,
        ISetPreferredCardPayload
    >({
        mutationFn: (payload: ISetPreferredCardPayload) =>
            setPreferredPaymentCard(payload),
        mutationKey: ["set-preferred-card"],
    });
}

// Hook for editing client insurance details
export function useEditClientInsuranceDetails(
    clientId: string,
    insuranceId: string
) {
    return useMutation<
        IApiResponse<IClientInsuranceDetails>,
        AxiosError<IApiResponse<IClientInsuranceDetails>>,
        IClientEditInsuranceDetails
    >({
        mutationKey: ["edit-insurance", clientId],
        mutationFn: (data: IClientEditInsuranceDetails) =>
            editClientInsuranceDetails(clientId, insuranceId, data),
    });
}

// Hook for deleting client insurance attachment
export function useDeleteClientInsuranceAttachment(
    clientId: string,
    documentId: string
) {
    interface IApiResponse {
        message: string;
        attachments: IClientInsuranceAttachment[];
    }
    return useMutation<IApiResponse, AxiosError<IApiResponse>, null>({
        mutationKey: ["delete-document", clientId],
        mutationFn: () => deleteInsuranceAttachment(clientId, documentId),
    });
}

// Hook for adding new client memo
export function useAddClientMemo(clientId: string) {
    return useMutation<
        IApiResponse<IClientMemo>,
        AxiosError<IApiResponse<IClientMemo>>,
        INewMemo
    >({
        mutationKey: ["add-memo", clientId],
        mutationFn: (data: INewMemo) => addClientMemo(clientId, data),
    });
}

export function useEditClientCurrentFee(clientId: string, feeId: string) {
    return useMutation<
        IApiResponse<IClientFee>,
        AxiosError<IApiResponse<IClientFee>>,
        IEditClientFee
    >({
        mutationKey: ["edit-fee"],
        mutationFn: (data: IEditClientFee) =>
            editClientCurrentFee(clientId, feeId, data),
    });
}

// Hook for adding client flag
export function useAddClientFlag(clientId: string) {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IAddClientFlag
    >({
        mutationKey: ["add-client-flag", clientId],
        mutationFn: (payload: IAddClientFlag) =>
            addClientFlag(clientId, payload),
    });
}

// Hook to delete client flag
export function useDeleteClientFlag(clientId: string, flagId: string) {
    interface IEditFlagResponse {
        client_flag_id: string;
        client_id: string;
        content: string;
        author: string;
    }
    return useMutation<
        IApiResponse<IEditFlagResponse>,
        AxiosError<IApiResponse<IEditFlagResponse>>,
        null
    >({
        mutationKey: ["delete-client-flag"],
        mutationFn: () => deleteClientFlags(clientId, flagId),
    });
}

// Hook to update client flag
export function useUpdateClientFlag(clientId: string, flagId: string) {
    return useMutation<
        IApiResponse<{ message: string }>,
        AxiosError<IApiResponse<{ message: string }>>,
        IAddClientFlag
    >({
        mutationKey: ["update-client-flag", flagId],
        mutationFn: (payload: IAddClientFlag) =>
            updateClientFlag(clientId, flagId, payload),
    });
}

// Hook to add client tag
export function useAddClientTag(clientId: string) {
    return useMutation<
        IApiResponse<IClientTag>,
        AxiosError<IApiResponse<IClientTag>>,
        ClientTagKey
    >({
        mutationKey: ["add-client-tag", clientId],
        mutationFn: (tagKey: ClientTagKey) => addClientTag(clientId, tagKey),
    });
}

// Hook to remove client tag
export function useRemoveClientTag(clientId: string) {
    return useMutation<
        IApiResponse<IClientTag>,
        AxiosError<IApiResponse<IClientTag>>,
        ClientTagKey
    >({
        mutationKey: ["remove-client-tag", clientId],
        mutationFn: (tagKey: ClientTagKey) => removeClientTag(clientId, tagKey),
    });
}

// Hook to add client session
export function useAddClientSession(clientId: string) {
    return useMutation<
        IApiResponse<IAddClientSessionResponse>,
        AxiosError<IApiResponse<IAddClientSessionResponse>>,
        IAddClientSession
    >({
        mutationKey: ["add-session"],
        mutationFn: (payload: IAddClientSession) =>
            addClientSession(clientId, payload),
    });
}

// Hook to edit client session
export function useEditClientSession(clientId: string) {
    return useMutation<
        IApiResponse<IEditClientSessionResponse>,
        AxiosError<IApiResponse<IEditClientSessionResponse>>,
        IEditClientSession
    >({
        mutationKey: ["edit-session"],
        mutationFn: (payload: IEditClientSession) =>
            editClientSession(clientId, payload),
    });
}

// Hook to delete client session
export function useDeleteClientSession(clientId: string) {
    return useMutation<
        IApiResponse<IDeleteClientSessionResponse>,
        AxiosError<IApiResponse<IDeleteClientSessionResponse>>,
        IDeleteClientSession
    >({
        mutationKey: ["delete-session", clientId],
        mutationFn: (payload: IDeleteClientSession) =>
            deleteClientSession(clientId, payload),
    });
}

export function useDeleteCreditCards(clientId: string) {
    return useMutation<
        IApiResponse<IDeleteCreditCardsResponse>,
        AxiosError<IApiResponse<IDeleteCreditCardsResponse>>,
        IDeleteCardsPayload
    >({
        mutationKey: ["delete-card"],
        mutationFn: (payload: IDeleteCardsPayload) =>
            deleteCreditCards(clientId, payload),
    });
}

export function useDeleteClientInsuranceInformation(clientId: string) {
    return useMutation<
        IApiResponse<IDeleteInsuranceInformationResponse>,
        AxiosError<IApiResponse<IDeleteInsuranceInformationResponse>>,
        IDeleteInsuranceInformationPayload
    >({
        mutationKey: ["delete-insurance-information"],
        mutationFn: (payload: IDeleteInsuranceInformationPayload) =>
            deleteClientInsuranceInformation(clientId, payload),
    });
}

export function useClientDataImportDiagnosis() {
    return useMutation<
        IApiResponse<IClientIntakeImportDiagnosisResponse>,
        AxiosError<IApiResponse<IClientIntakeImportDiagnosisResponse>>,
        IClientIntakeImportDiagnosisPayload
    >({
        mutationKey: ["client-intake-import-diagnosis"],
        mutationFn: (payload: IClientIntakeImportDiagnosisPayload) =>
            clientDataImportDiagnosis(payload),
        retry: 0,
    });
}

export function useGeneratePaymentLink() {
    return useMutation<
        IApiResponse<IGeneratePaymentLinkResponse>,
        AxiosError<IApiResponse<IGeneratePaymentLinkResponse>>,
        IGeneratePaymentLinkPayload
    >({
        mutationKey: ["generate-payment-link"],
        mutationFn: (payload: IGeneratePaymentLinkPayload) =>
            generatePaymentLink(payload),
    });
}

export function useUpdateIntakeClient() {
    return useMutation<
        IApiResponse<null>,
        AxiosError<IApiResponse<null>>,
        { intakeClientId: string; payload: IUpdateClientOnIntake }
    >({
        mutationKey: ["update-intake-client"],
        mutationFn: ({ intakeClientId, payload }) =>
            updateIntakeClient(intakeClientId, payload),
    });
}

export function useAddCollectionsNote(clientId: string) {
    return useMutation<
        IApiResponse<ICollectionsNoteRecord>,
        AxiosError<IApiResponse<null>>,
        { description: string }
    >({
        mutationKey: ["add-collections-note", clientId],
        mutationFn: (payload) => addCollectionsNote(clientId, payload),
    });
}

export function useEditCollectionsNote() {
    return useMutation<
        IApiResponse<ICollectionsNoteRecord>,
        AxiosError<IApiResponse<null>>,
        { collectionsNoteId: string; description: string }
    >({
        mutationKey: ["edit-collections-note"],
        mutationFn: ({ collectionsNoteId, description }) =>
            editCollectionsNote(collectionsNoteId, { description }),
    });
}

export function useDeleteCollectionsNote() {
    return useMutation<
        IApiResponse<{ collections_note_id: string }>,
        AxiosError<IApiResponse<null>>,
        string
    >({
        mutationKey: ["delete-collections-note"],
        mutationFn: (collectionsNoteId) =>
            deleteCollectionsNote(collectionsNoteId),
    });
}

export function useAddCollectionsNoteDocuments() {
    return useMutation<
        IApiResponse<ICollectionsNoteDocument[]>,
        AxiosError<IApiResponse<null>>,
        { collectionsNoteId: string; formData: FormData }
    >({
        mutationKey: ["add-collections-note-documents"],
        mutationFn: ({ collectionsNoteId, formData }) =>
            addCollectionsNoteDocuments(collectionsNoteId, formData),
    });
}

export function useDeleteCollectionsNoteDocuments() {
    return useMutation<
        IApiResponse<string[]>,
        AxiosError<IApiResponse<null>>,
        { collectionsNoteId: string; documentIds: string[] }
    >({
        mutationKey: ["delete-collections-note-documents"],
        mutationFn: ({ collectionsNoteId, documentIds }) =>
            deleteCollectionsNoteDocuments(collectionsNoteId, documentIds),
    });
}

export function useAddCommunicationNote(clientId: string) {
    return useMutation<
        IApiResponse<ICommunicationNote>,
        AxiosError<IApiResponse<null>>,
        INewCommunicationNoteRecord
    >({
        mutationKey: ["add-communication-note", clientId],
        mutationFn: (payload) => addCommunicationNote(clientId, payload),
    });
}

export function useEditCommunicationNote(clientId: string) {
    return useMutation<
        IApiResponse<ICommunicationNote>,
        AxiosError<IApiResponse<null>>,
        { communicationNoteId: string; body: string }
    >({
        mutationKey: ["edit-communication-note", clientId],
        mutationFn: ({ communicationNoteId, body }) =>
            editCommunicationNote(clientId, communicationNoteId, { body }),
    });
}

export function useDeleteCommunicationNote(clientId: string) {
    return useMutation<
        IApiResponse<{ communication_note_id: string }>,
        AxiosError<IApiResponse<null>>,
        string
    >({
        mutationKey: ["delete-communication-note", clientId],
        mutationFn: (communicationNoteId) =>
            deleteCommunicationNote(clientId, communicationNoteId),
    });
}
